import React, { useEffect, useState } from 'react'
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Paper, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../../supabaseClient'
import { useAuth } from '../../../auth/AuthContext'
import { Class, ClassStatus } from '../../../types'

const STATUS_COLOR: Record<ClassStatus, 'default' | 'info' | 'success' | 'warning'> = {
  upcoming:  'info',
  active:    'success',
  completed: 'default',
  archived:  'warning',
}

interface ClassRow extends Class {
  enrolled: number
}

interface CreateForm {
  name:          string
  academic_year: string
  start_date:    string
  end_date:      string
  capacity:      string
}

const EMPTY_FORM: CreateForm = {
  name:          '',
  academic_year: '2025-2026',
  start_date:    '',
  end_date:      '',
  capacity:      '10',
}

function fmtDate(s: string) {
  if (!s) return '\u2014'
  // DATE strings (YYYY-MM-DD) need UTC parsing to avoid timezone shift
  const [y, m, d] = s.split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function ClassesPage() {
  const { session } = useAuth()
  const navigate    = useNavigate()
  const [classes,  setClasses]  = useState<ClassRow[]>([])
  const [loading,  setLoading]  = useState(true)
  const [open,     setOpen]     = useState(false)
  const [form,     setForm]     = useState<CreateForm>(EMPTY_FORM)
  const [saving,   setSaving]   = useState(false)
  const [error,    setError]    = useState<string | null>(null)

  async function load() {
    const [{ data: classData }, { data: enrollData }] = await Promise.all([
      supabase.from('class').select('*').order('start_date', { ascending: false }),
      supabase.from('class_enrollment').select('class_id').eq('status', 'active'),
    ])

    const countByClass: Record<string, number> = {}
    for (const e of enrollData ?? []) {
      countByClass[e.class_id] = (countByClass[e.class_id] ?? 0) + 1
    }

    setClasses(
      (classData ?? []).map(c => ({ ...c, enrolled: countByClass[c.class_id] ?? 0 }))
    )
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  function set(field: keyof CreateForm) {
    return (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm(f => ({ ...f, [field]: e.target.value }))
  }

  async function handleCreate() {
    if (!session) return
    setSaving(true)
    setError(null)

    const { error } = await supabase.from('class').insert({
      name:          form.name,
      academic_year: form.academic_year,
      start_date:    form.start_date,
      end_date:      form.end_date,
      capacity:      Number(form.capacity),
      instructor_id: session.user.id,
      status:        'upcoming',
      created_by:    session.user.id,
    })

    if (error) { setError(error.message); setSaving(false); return }
    setOpen(false)
    setForm(EMPTY_FORM)
    setSaving(false)
    load()
  }

  return (
    <Box sx={{ p: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Classes</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
          New Class
        </Button>
      </Box>

      <Paper>
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
        ) : classes.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">No classes yet. Create the first one.</Typography>
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Academic Year</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Start</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>End</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Enrolled</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {classes.map(c => (
                <TableRow key={c.class_id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/classes/${c.class_id}`)}>
                  <TableCell sx={{ fontWeight: 500 }}>{c.name}</TableCell>
                  <TableCell>{c.academic_year}</TableCell>
                  <TableCell>{fmtDate(c.start_date)}</TableCell>
                  <TableCell>{fmtDate(c.end_date)}</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {c.enrolled}
                      <Typography component="span" variant="caption" color="text.secondary"> / {c.capacity}</Typography>
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={c.status} color={STATUS_COLOR[c.status]} size="small" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Create Class</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          {error && <Typography color="error" variant="body2">{error}</Typography>}
          <TextField label="Class Name" value={form.name} onChange={set('name')} required fullWidth
            placeholder="e.g. Summer 2026 Cohort A" />
          <TextField label="Academic Year" value={form.academic_year} onChange={set('academic_year')} required fullWidth />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Start Date" type="date" value={form.start_date} onChange={set('start_date')}
              slotProps={{ inputLabel: { shrink: true } }} required fullWidth
            />
            <TextField
              label="End Date" type="date" value={form.end_date} onChange={set('end_date')}
              slotProps={{ inputLabel: { shrink: true } }} required fullWidth
            />
          </Box>
          <TextField label="Capacity" type="number" value={form.capacity} onChange={set('capacity')} required fullWidth />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!form.name || !form.start_date || !form.end_date || saving}
            onClick={handleCreate}
          >
            {saving ? <CircularProgress size={18} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
